import { useId, useState } from "react";
import type { Row } from "../api";
import {
  friendlyFieldLabel,
  friendlyValue,
  hasTechnicalDependency,
  parseIntakeNumber,
  resolveIntakeSchema,
  validateIntakeValue,
} from "../intake-values";
import {
  fieldTargetId,
  validateTaxFields,
  type FieldIssue,
} from "../field-validation";
import { FieldError, ValidationSummary } from "./ValidationSummary";
import { normalizeTaxAge } from "../tax-field-values";
import { addressBinding } from "../address-model";
import { TaxFields } from "./TaxFields";

export interface IntakeValueFieldsProps {
  schema: Row;
  value: unknown;
  onChange: (value: unknown) => void;
  label: string;
  disabled?: boolean;
  definitions?: Row;
  rootId?: string;
  sectionLabel?: string;
}
const isObject = (value: unknown): value is Row =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const unavailable =
  "This information needs a connected capture or review workflow. It cannot be entered here yet.";

/** Typed local draft inputs. No value, answer state, evidence or review is supplied on the user's behalf. */
export function IntakeValueFields(props: IntakeValueFieldsProps) {
  const instanceId = useId(),
    rootId = props.rootId ?? instanceId;
  if (hasTechnicalDependency(props.schema, props.definitions))
    return (
      <div className="intake-field-unavailable" role="status">
        <strong>{props.label}</strong>
        <p>{unavailable}</p>
      </div>
    );
  const issues = validateTaxFields(props.schema, props.value, {
    rootId,
    rootLabel: props.label,
    sectionId: rootId,
    sectionLabel: props.sectionLabel ?? props.label,
    definitions: props.definitions,
    presentation: "legacy",
  });
  return (
    <>
      <ValidationSummary issues={issues} />
      {addressBinding(
        resolveIntakeSchema(props.schema, props.definitions) ?? {},
      ) ? (
        <TaxFields
          {...props}
          rootId={rootId}
          sectionId={rootId}
          sectionLabel={props.sectionLabel ?? props.label}
        />
      ) : (
        <ValueControl
          {...props}
          rootId={rootId}
          depth={0}
          path={[]}
          issues={issues}
        />
      )}
    </>
  );
}

function ValueControl({
  schema,
  value,
  onChange,
  label,
  disabled = false,
  definitions,
  depth,
  rootId = "",
  path,
  issues,
}: IntakeValueFieldsProps & {
  depth: number;
  path: string[];
  issues: FieldIssue[];
}) {
  const id = fieldTargetId(rootId, path),
    issue = issues.find((item) => item.targetId === id);
  const fieldProps = {
    "aria-invalid": Boolean(issue),
    "aria-describedby": issue ? `${id}-error` : undefined,
  };
  const [nullableEntry, setNullableEntry] = useState(
    value !== undefined && value !== null,
  );
  const resolved = resolveIntakeSchema(schema, definitions);
  const unavailableControl = (
    <div className="intake-field-unavailable" role="status">
      <strong>{label}</strong>
      <p>{unavailable}</p>
    </div>
  );
  if (!resolved || depth > 24) return unavailableControl;
  const childProps = {
    disabled,
    definitions,
    depth: depth + 1,
    rootId,
    path,
    issues,
  };
  const stateful = resolved["x-intake-stateful"];
  if (stateful) {
    const wrapped = isObject(value) && typeof value.state === "string";
    const state = wrapped ? value.state : value === undefined ? "" : "KNOWN";
    const answer = wrapped ? value.value : value;
    return (
      <fieldset
        id={state !== "KNOWN" ? id : undefined}
        className="intake-value-group"
        disabled={disabled}
      >
        <legend>{label}</legend>
        <label htmlFor={`${id}-status`}>Answer status</label>
        <select
          id={`${id}-status`}
          {...(state !== "KNOWN" ? fieldProps : {})}
          value={state}
          onChange={(event) => {
            const next = event.target.value;
            onChange(
              next === ""
                ? undefined
                : next === "KNOWN"
                  ? {
                      state: next,
                      ...(state === "KNOWN" && answer !== undefined
                        ? { value: answer }
                        : {}),
                    }
                  : { state: next },
            );
          }}
        >
          <option value="">Choose an answer status</option>
          {stateful.states.map((option: string) => (
            <option key={option} value={option}>
              {friendlyValue(option)}
            </option>
          ))}
        </select>
        {state !== "KNOWN" && <FieldError issue={issue} />}
        {state === "KNOWN" && (
          <ValueControl
            {...childProps}
            schema={stateful.valueSchema}
            value={answer}
            label="Your answer"
            onChange={(next) =>
              onChange(wrapped ? { ...value, value: next } : next)
            }
          />
        )}
        {state === "UNKNOWN" && (
          <p>Leave this question open until the information is available.</p>
        )}
        {state === "CONFLICT" && (
          <p>
            Keep the differing information for follow-up. No answer is treated
            as confirmed.
          </p>
        )}
        <small>
          Draft answer only. Saving does not verify supporting documents.
        </small>
      </fieldset>
    );
  }

  const variants: Row[] | undefined =
    resolved.anyOf ??
    resolved.oneOf ??
    (Array.isArray(resolved.type)
      ? resolved.type.map((type: string) => ({ ...resolved, type }))
      : undefined);
  if (variants) {
    const nonNull = variants.filter(
      (branch) => resolveIntakeSchema(branch, definitions)?.type !== "null",
    );
    if (nonNull.length === 1 && nonNull.length !== variants.length) {
      const mode =
        value === null
          ? "none"
          : value !== undefined || nullableEntry
            ? "entry"
            : "";
      return (
        <fieldset
          id={mode !== "entry" ? id : undefined}
          className="intake-value-group"
          disabled={disabled}
        >
          <legend>{label}</legend>
          <label htmlFor={`${id}-presence`}>Information available</label>
          <select
            id={`${id}-presence`}
            {...(mode !== "entry" ? fieldProps : {})}
            value={mode}
            onChange={(event) => {
              const next = event.target.value;
              setNullableEntry(next === "entry");
              onChange(next === "none" ? null : undefined);
            }}
          >
            <option value="">Choose an explicit answer</option>
            <option value="entry">Enter a value</option>
            <option value="none">No value supplied</option>
          </select>
          {mode !== "entry" && <FieldError issue={issue} />}
          {mode === "entry" && (
            <ValueControl
              {...childProps}
              schema={nonNull[0]}
              value={value === null ? undefined : value}
              label={label}
              onChange={onChange}
            />
          )}
        </fieldset>
      );
    }
    return unavailableControl;
  }
  const options = Array.isArray(resolved.enum)
    ? resolved.enum
    : Object.prototype.hasOwnProperty.call(resolved, "const")
      ? [resolved.const]
      : undefined;
  if (
    options &&
    options.every(
      (option: unknown) =>
        option === null ||
        ["string", "number", "boolean"].includes(typeof option),
    )
  ) {
    const selected = options.findIndex((option: unknown) =>
      Object.is(option, value),
    );
    return (
      <div className="intake-value-field">
        <label htmlFor={id}>{label}</label>
        <select
          id={id}
          {...fieldProps}
          disabled={disabled}
          value={selected < 0 ? "" : String(selected)}
          onChange={(event) =>
            onChange(
              event.target.value === ""
                ? undefined
                : options[Number(event.target.value)],
            )
          }
        >
          <option value="">Choose an explicit answer</option>
          {options.map((option: unknown, index: number) => (
            <option key={index} value={index}>
              {friendlyValue(option)}
            </option>
          ))}
        </select>
        <FieldError issue={issue} />
      </div>
    );
  }
  if (resolved.type === "boolean")
    return (
      <div className="intake-value-field">
        <label htmlFor={id}>{label}</label>
        <select
          id={id}
          {...fieldProps}
          disabled={disabled}
          value={value === true ? "yes" : value === false ? "no" : ""}
          onChange={(event) =>
            onChange(
              event.target.value === ""
                ? undefined
                : event.target.value === "yes",
            )
          }
        >
          <option value="">Choose Yes or No</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
        <FieldError issue={issue} />
      </div>
    );
  if (resolved.type === "object" && isObject(resolved.properties)) {
    const hasAge = Boolean(
      resolved.properties.date_of_birth && resolved.properties.age_for_2025,
    );
    const normalized = hasAge ? normalizeTaxAge(value) : value,
      current = isObject(normalized) ? normalized : {};
    const change = (key: string, next: unknown) => {
      const updated = { ...current };
      if (next === undefined) delete updated[key];
      else updated[key] = next;
      onChange(hasAge ? normalizeTaxAge(updated) : updated);
    };
    return (
      <fieldset
        id={id}
        {...fieldProps}
        className="intake-value-group"
        disabled={disabled}
      >
        <legend>{label}</legend>
        <FieldError issue={issue} />
        {Object.entries(resolved.properties).map(([key, child]) =>
          hasAge && key === "age_for_2025" ? (
            <div className="intake-value-field" key={key}>
              <label htmlFor={fieldTargetId(rootId, [...path, key])}>
                Age for the tax year
              </label>
              <input
                id={fieldTargetId(rootId, [...path, key])}
                type="text"
                readOnly
                value={
                  typeof current[key] === "number" ? String(current[key]) : ""
                }
                placeholder="Enter date of birth first"
                aria-describedby={`${fieldTargetId(rootId, [...path, key])}-derived`}
              />
              <small id={`${fieldTargetId(rootId, [...path, key])}-derived`}>
                Calculated from date of birth for tax year 2025. Correct the
                date of birth to change this age.
              </small>
            </div>
          ) : (
            <ValueControl
              {...childProps}
              path={[...path, key]}
              key={key}
              schema={child as Row}
              value={current[key]}
              label={`${(child as Row)["x-intake-label"] ?? friendlyFieldLabel(key)}${resolved.required?.includes(key) ? "" : " (optional)"}`}
              onChange={(next) => change(key, next)}
            />
          ),
        )}
      </fieldset>
    );
  }
  if (resolved.type === "array" && isObject(resolved.items)) {
    const items = Array.isArray(value) ? value : [];
    return (
      <fieldset
        id={id}
        {...fieldProps}
        className="intake-value-group"
        disabled={disabled}
      >
        <legend>{label}</legend>
        <FieldError issue={issue} />
        {value === undefined && (
          <p>No answer recorded. Add an item or explicitly record no items.</p>
        )}
        {value !== undefined && !items.length && <p>No items recorded.</p>}
        {items.map((item, index) => (
          <div className="intake-value-item" key={index}>
            <ValueControl
              {...childProps}
              path={[...path, String(index)]}
              schema={resolved.items}
              value={item}
              label={`Item ${index + 1}`}
              onChange={(next) =>
                onChange(
                  items.map((previous, itemIndex) =>
                    itemIndex === index ? next : previous,
                  ),
                )
              }
            />
            <button
              className="btn"
              type="button"
              disabled={disabled}
              onClick={() =>
                onChange(items.filter((_, itemIndex) => itemIndex !== index))
              }
            >
              Remove item {index + 1}
            </button>
          </div>
        ))}
        <div className="form-actions">
          <button
            className="btn"
            type="button"
            disabled={
              disabled ||
              (resolved.maxItems !== undefined &&
                items.length >= resolved.maxItems)
            }
            onClick={() => onChange([...items, undefined])}
          >
            Add item
          </button>
          {!items.length &&
            value === undefined &&
            (resolved.minItems ?? 0) === 0 && (
              <button
                className="btn"
                type="button"
                disabled={disabled}
                onClick={() => onChange([])}
              >
                Record no items
              </button>
            )}
        </div>
      </fieldset>
    );
  }
  if (resolved.type === "null")
    return (
      <div className="intake-value-field">
        <label htmlFor={id}>{label}</label>
        <select
          id={id}
          {...fieldProps}
          disabled={disabled}
          value={value === null ? "none" : ""}
          onChange={(event) =>
            onChange(event.target.value === "none" ? null : undefined)
          }
        >
          <option value="">Choose an explicit answer</option>
          <option value="none">No value supplied</option>
        </select>
        <FieldError issue={issue} />
      </div>
    );
  if (["string", "number", "integer"].includes(resolved.type)) {
    const numeric = resolved.type !== "string";
    const amount =
      resolved.type === "string" &&
      typeof resolved.pattern === "string" &&
      resolved.pattern.includes("[0-9]{2}");
    const validation =
      value === undefined
        ? { valid: true, errors: [] }
        : validateIntakeValue(value, resolved, definitions);
    const hint = amount
      ? "Enter the exact amount with two decimal places. Do not round."
      : resolved.format === "date-time"
        ? "Include the time-zone offset, for example 2025-12-31T17:00:00-08:00."
        : numeric
          ? resolved.type === "integer"
            ? "Enter a whole number. Values are not rounded."
            : "Values must be stored without rounding."
          : "";
    return (
      <div className="intake-value-field">
        <label htmlFor={id}>{label}</label>
        <input
          id={id}
          type={resolved.format === "date" ? "date" : "text"}
          disabled={disabled}
          value={
            value === undefined || value === null
              ? ""
              : typeof value === "string" || typeof value === "number"
                ? String(value)
                : ""
          }
          inputMode={numeric || amount ? "decimal" : undefined}
          maxLength={resolved.maxLength}
          aria-invalid={Boolean(issue) || !validation.valid}
          aria-describedby={
            [issue ? `${id}-error` : "", hint ? `${id}-help` : ""]
              .filter(Boolean)
              .join(" ") || undefined
          }
          onChange={(event) => {
            const text = event.target.value;
            if (!numeric) onChange(text);
            else {
              const parsed = parseIntakeNumber(
                text,
                resolved.type === "integer",
              );
              onChange(
                parsed.valid ? parsed.value : text === "" ? undefined : text,
              );
            }
          }}
        />
        <FieldError issue={issue} />
        {hint && (
          <div id={`${id}-help`}>
            <small>{hint}</small>
          </div>
        )}
      </div>
    );
  }
  return unavailableControl;
}
